import type { ContainerType } from "~/container";
import type { IApi } from "~/interfaces/IApi";
import type { IDraftService } from "@saif-reporting/domain/interfaces/services/IDraftService.js";
import { ReportDto } from "@saif-reporting/domain/dtos/models/ReportDto.js";
import type { Paginated } from "@saif-reporting/domain/dtos/Paginated.js";
import type { ReportListQuery, ReportCreateRequest } from "@saif-reporting/domain/dtos/reportDtos.js";
import { plainToInstance } from "class-transformer";
import { DraftReportDto } from "@saif-reporting/domain/dtos/models/DraftReportDto.js";
import type { DraftUpdateRequest } from "@saif-reporting/domain/dtos/draftsDto.js";
import type { MediaDto } from "@saif-reporting/domain/dtos/models/MediaDto.js";

export class DraftReportService implements IDraftService {
  private readonly api: IApi;
  constructor({ baseApi }: ContainerType) {
    this.api = baseApi;
  }

  async getAll(query: ReportListQuery): Promise<Paginated<DraftReportDto>> {
    return await this.api.$fetch<Paginated<DraftReportDto>>("/drafts");
  }

  async getById(id: number): Promise<DraftReportDto> {
    const draft = await this.api.$fetch<DraftReportDto>(`/drafts/${id}`);
    return plainToInstance(DraftReportDto, draft);
  }

  async getCount(): Promise<{ count: number }> {
    const results = await this.api.$fetch<{ count: number }>(`/drafts/get-count`);
    return results;
  }

  async create(): Promise<DraftReportDto> {
    return await this.api.$fetch<DraftReportDto>("/drafts", { method: "post" });
  }

  async saveDraft(id: number, dto: DraftUpdateRequest): Promise<DraftReportDto> {
    return await this.api.$fetch<DraftReportDto>(`/drafts/${id}/save`, {
      method: "put",
      body: dto,
    });
  }

  async saveAndSendReport(id: number, dto: DraftUpdateRequest): Promise<ReportDto> {
    return await this.api.$fetch<ReportDto>(`/drafts/${id}/send`, {
      method: "put",
      body: dto,
    });
  }

  async delete(id: number): Promise<DraftReportDto> {
    return await this.api.$fetch<DraftReportDto>(`/drafts/${id}`, {
      method: "delete",
    });
  }

  async saveMedia(id: number, file: File): Promise<MediaDto> {
    const formdata = new FormData();
    formdata.append("file", file);
    const results = await this.api.$fetch<MediaDto>(`/drafts/${id}/save-media`, {
      method: "post",
      body: formdata,
    });

    return results;
  }

  async deleteMedia(id: number, mediaId: number): Promise<MediaDto> {
    return await this.api.$fetch<MediaDto>(`/drafts/${id}/${mediaId}`, {
      method: "delete",
    });
  }
}
